import { excludeFromAmbientOcclusion } from "@project/emerge/src/new/ProductViewerPostProcessing";
import { GROUND_SHADOW_Y } from "@project/emerge/src/new/ProductViewerStage";
import { StandardEnvironment } from "@project/emerge/src/new/StandardEnvironment";
import { Grid } from "@react-three/drei";
import { useThree } from "@react-three/fiber";
import { useLayoutEffect, useRef } from "react";
import type { Mesh } from "three";
import { applyGridPixelDensityFade } from "./gridPixelDensityFade";

/**
 * The grid sits just below the ground shadow plane. Everything the shadow
 * camera can see is drawn into the shadow texture, and drei's grid renders
 * back faces — so at floor level it paints its own bright lines into the
 * shadow (a white flash whenever depth precision lets it through). Keeping it
 * behind that camera is what excludes it; `allowOverride` would not, since
 * that only skips the depth-material swap, not the draw.
 *
 * The grid is a shader-driven quad, so it also has to be kept out of the
 * ambient occlusion G-buffer — see {@link excludeFromAmbientOcclusion}.
 */
const GRID_Y = GROUND_SHADOW_Y - 0.003;

// Transparent floor layers: floor patch (1), grid, contact pool (2), cast shadow (3).
// Keep the grid below the shadow camera physically; only its blend order changes.
const GRID_RENDER_ORDER = 1.5;

const useGridRef = () => {
  const ref = useRef<Mesh>(null);
  const invalidate = useThree((state) => state.invalidate);
  useLayoutEffect(() => {
    const grid = ref.current;
    if (!grid) return;
    applyGridPixelDensityFade(grid);
    excludeFromAmbientOcclusion(grid);
    // Lines overlay the floor patch, not the product. Keep depth testing, but
    // never let a translucent annotation write depth into the AO or other decals.
    const materials = Array.isArray(grid.material)
      ? grid.material
      : [grid.material];
    for (const material of materials) material.depthWrite = false;
    invalidate();
  }, [invalidate]);
  return ref;
};

/**
 * Scene that displays a grid on the floor with the standard environment
 */
export const GridFloorScene = () => {
  const fineGridRef = useGridRef();
  const coarseGridRef = useGridRef();

  return (
    <>
      {/* Setting and clearing `scene.environment` is the environment's own
          job — there is no `onCreated` hook to hang extra bookkeeping off. */}
      <StandardEnvironment isGlobal={true} />

      {/* Grid on the floor - positioned to align with scaled products */}
      <Grid
        ref={fineGridRef}
        renderOrder={GRID_RENDER_ORDER}
        position={[0, GRID_Y, 0]}
        args={[10, 10]} // Width and height of the grid
        cellSize={0.01} // Size of each cell (10x kleiner: 0.25 -> 0.025)
        cellThickness={0.5} // Thickness of the grid lines (hellgraue feine Linien)
        cellColor="#ccc" // Color of the grid lines (hellgrau für feine Unterteilung)
        sectionSize={0.1} // Size of each section (mittlere Linien alle 0.25)
        sectionThickness={0.6} // Thickness of the section lines
        sectionColor="#999" // Color of the section lines (mittelgrau)
        fadeDistance={20} // Distance at which the grid begins to fade
        infiniteGrid={true} // Whether the grid extends infinitely
        fadeStrength={1.5} // Strength of the fade effect
      />

      {/* Hauptgrid für grobe Orientierung */}
      <Grid
        ref={coarseGridRef}
        renderOrder={GRID_RENDER_ORDER}
        position={[0, GRID_Y, 0]}
        args={[10, 10]} // Width and height of the grid
        cellSize={1} // Hauptraster alle 1 Einheit
        cellThickness={1.2} // Dickere Linien für Hauptraster
        cellColor="#666" // Dunkelgrau für Hauptlinien
        sectionSize={0} // Keine Sections
        fadeDistance={20} // Distance at which the grid begins to fade
        infiniteGrid={true} // Whether the grid extends infinitely
        fadeStrength={1.5} // Strength of the fade effect
      />
    </>
  );
};
